import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../App";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { MessageSquare, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export default function InboxPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? enUS : es;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await api.get("/messages/conversations");
      setConversations(response.data || []);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="inbox-page">
      <div className="container-main py-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-6">
          {t("inbox.title")}
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
          </div>
        ) : conversations.length > 0 ? (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <Link
                key={conv.booking_id}
                to={`/inbox/${conv.booking_id}`}
                className="card-base p-4 hover-lift flex items-center gap-4"
                data-testid={`conversation-${conv.booking_id}`}
              >
                <div className="relative">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={conv.other_user?.picture} />
                    <AvatarFallback className="bg-terracotta text-white">
                      {conv.other_user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  {conv.unread_count > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-terracotta rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {conv.unread_count}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold truncate">{conv.other_user?.name}</p>
                    {conv.last_message && (
                      <span className="text-xs text-gray-500">
                        {formatDistanceToNow(new Date(conv.last_message.created_at), {
                          addSuffix: true,
                          locale: dateLocale,
                        })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 truncate">
                    {conv.vehicle?.make} {conv.vehicle?.model}
                  </p>
                  {conv.last_message && (
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {conv.last_message.content}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t("inbox.emptyTitle")}
            </h3>
            <p className="text-gray-500">
              {t("inbox.emptyBody")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
